import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Offcanvas } from 'react-bootstrap';
import { useAuth } from "../Context/auth";
import MenuLateral from "../Components/MenuPrincipal/MenuLateral";
import ProducaoGrid from '../Components/ProducaoGrid/ProducaoGrid';
import Pagination from "../Components/Pagination/Pagination";
import './Producao.css';

// === IMPORTS DOS MODAIS ===
import ModalConcluirProducao from '../Components/ProducaoForm/ModalConcluirProducao';
import ModalEstornarConclusao from '../Components/ProducaoForm/ModalEstornarConclusao';
// === FIM IMPORTS DOS MODAIS ===

// Função de formatar data
const formatarData = (isoString) => {
    if (!isoString) return '—';
    const data = isoString.split('T')[0];
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
};

function Producao() {
    const { currentUser, appUserData } = useAuth();
    const navigate = useNavigate();

    // Data States
    const [producoes, setProducoes] = useState([]);
    const [producoesFiltradas, setProducoesFiltradas] = useState([]);
    const [listaProdutos, setListaProdutos] = useState([]); // Para o filtro de produtos

    // UI States
    const [loading, setLoading] = useState(true);
    const [loadingAction, setLoadingAction] = useState(false);
    const [triggerFetch, setTriggerFetch] = useState(0);

    // Filters States
    const [showFiltros, setShowFiltros] = useState(false);
    const [filtrosForm, setFiltrosForm] = useState({
        status: [],
        numero: '',
        descricao: '',
        produtoId: ''
    });
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        status: [],
        numero: '',
        descricao: '',
        produtoId: ''
    });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // === STATES PARA CONTROLE DOS MODAIS ===
    const [showConcluirModal, setShowConcluirModal] = useState(false);
    const [showEstornarModal, setShowEstornarModal] = useState(false);
    const [selectedProducao, setSelectedProducao] = useState(null);
    const [detailedProducaoData, setDetailedProducaoData] = useState(null);

    // Status Options for Filter
    const statusOptions = ['Planejada', 'Em Produção', 'Concluído parcial', 'Concluída', 'Cancelada'];

    // Verifica permissão de leitura
    useEffect(() => {
        if (appUserData && appUserData['ler_producao'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar a tela de produção.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    const apiCall = useCallback(async (url, method = 'GET', data = null) => {
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const config = {
            method,
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        };
        if (data && method !== 'GET') config.body = JSON.stringify(data);
        const response = await fetch(`${API_URL}${url}`, config);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Falha na requisição para ${url} (${response.status})`);
        }
        return response.status !== 204 ? response.json() : null;
    }, [currentUser]);

    // Busca Ordens de Produção e Produtos
    const fetchData = useCallback(async () => {
        if (!currentUser) { setLoading(false); return; }
        setLoading(true);
        try {
            // Busca Produções
            const dataProducao = await apiCall(`/api/producao`);
            const sortedData = (Array.isArray(dataProducao) ? dataProducao : []).sort((a, b) => b.id - a.id);
            setProducoes(sortedData);
            setProducoesFiltradas(sortedData); // Initialize filtered list immediately

            // Busca Produtos de forma independente (não bloqueante)
            if (listaProdutos.length === 0) {
                apiCall(`/api/produtos`)
                    .then(data => setListaProdutos(data || []))
                    .catch(err => console.error("Erro ao carregar produtos:", err));
            }

        } catch (error) {
            console.error("Erro ao buscar dados: ", error);
            Swal.fire("Erro", "Não foi possível carregar as Ordens de Produção.", "error");
            setProducoes([]); // Ensure it's an array on error
        } finally {
            setLoading(false);
        }
    }, [currentUser, apiCall, listaProdutos.length]);

    // Refresh Grid
    useEffect(() => {
        fetchData();
    }, [fetchData, triggerFetch]);

    // Lógica de Filtragem no Frontend
    useEffect(() => {
        if (!producoes) return; // Guard clause

        let resultado = [...producoes];

        // Filtro de Status
        if (filtrosAplicados.status && filtrosAplicados.status.length > 0) {
            resultado = resultado.filter(p => filtrosAplicados.status.includes(p.status));
        }

        // Filtro de Número
        if (filtrosAplicados.numero) {
            resultado = resultado.filter(p => (p.numero || '').toString().includes(filtrosAplicados.numero));
        }

        // Filtro de Descrição
        if (filtrosAplicados.descricao) {
            const termo = filtrosAplicados.descricao.toLocaleLowerCase();
            resultado = resultado.filter(p => (p.descricao || '').toLocaleLowerCase().includes(termo));
        }

        // Filtro de Produto
        if (filtrosAplicados.produtoId) {
            const filterId = Number(filtrosAplicados.produtoId);
            resultado = resultado.filter(p =>
                p.produto_ids && Array.isArray(p.produto_ids) && p.produto_ids.includes(filterId)
            );
        }

        setProducoesFiltradas(resultado);
        setCurrentPage(1); // Resetar para página 1 ao filtrar
    }, [producoes, filtrosAplicados]);


    // Handlers de Filtro
    const handleFiltroChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFiltrosForm(prev => {
                const newStatus = checked
                    ? [...prev.status, value]
                    : prev.status.filter(s => s !== value);
                return { ...prev, status: newStatus };
            });
        } else {
            setFiltrosForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleAplicarFiltros = () => {
        setFiltrosAplicados(filtrosForm);
        setShowFiltros(false);
    };

    const handleLimparFiltros = () => {
        const emptyFilters = { status: [], numero: '', descricao: '', produtoId: '' };
        setFiltrosForm(emptyFilters);
        setFiltrosAplicados(emptyFilters);
        setShowFiltros(false);
    };

    const handleKeyDownFiltros = (e) => {
        if (e.key === 'Enter') handleAplicarFiltros();
    };

    // Handler Deletar
    const deleteProducao = async (id) => {
        if (!appUserData || !appUserData['excluir_producao']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para excluir ordens de produção.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        const op = producoes.find(p => p.id === id);
        if (!op) return;

        if (op.status !== 'Planejada') {
            Swal.fire("Atenção!", 'Só é possível excluir Ordens de Produção com status "Planejada".', "warning");
            return;
        }

        Swal.fire({
            title: "Você tem certeza?",
            text: `Excluir a OP #${op.numero}? Itens e insumos (não baixados) serão removidos.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2185c6",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sim, excluir!",
            cancelButtonText: "Cancelar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoadingAction(true);
                try {
                    await apiCall(`/api/producao/${id}`, 'DELETE');
                    Swal.fire("Excluído!", "A Ordem de Produção foi deletada.", "success");
                    setTriggerFetch(prev => prev + 1);
                } catch (error) {
                    console.error("Erro ao deletar OP:", error);
                    Swal.fire("Erro!", error.message || "Não foi possível deletar a OP.", "error");
                } finally {
                    setLoadingAction(false);
                }
            }
        });
    }

    // Handlers Modais e Ações
    const handleConcluirClick = async (producaoId) => {
        if (!appUserData || !appUserData['editar_producao']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para concluir produção.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        setShowConcluirModal(true);
        setSelectedProducao(producoes.find(p => p.id === producaoId) || null);
        setDetailedProducaoData(null);
        try {
            const data = await apiCall(`/api/producao/${producaoId}`);
            if (data && data.itens_produzir) {
                setDetailedProducaoData(data);
            } else {
                throw new Error("Não foi possível carregar os itens da produção.");
            }
        } catch (error) {
            Swal.fire("Erro", error.message, "error");
            setShowConcluirModal(false);
            setSelectedProducao(null);
        } finally {

        }
    };

    const handleEstornarClick = (producaoId) => {
        if (!appUserData || !appUserData['editar_producao']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para estornar produção.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        const op = producoes.find(p => p.id === producaoId);
        if (op) {
            setSelectedProducao(op);
            setShowEstornarModal(true);
        }
    };

    const handleCancelarClick = (producaoId) => {
        if (!appUserData || !appUserData['editar_producao']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para cancelar produção.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        const op = producoes.find(p => p.id === producaoId);
        if (!op) return;

        if (op.status !== 'Planejada') {
            Swal.fire("Atenção!", 'Só é possível cancelar Ordens de Produção com status "Planejada".', "warning");
            return;
        }

        Swal.fire({
            title: "Cancelar Produção?",
            text: `Deseja cancelar a OP #${op.numero}? Status será 'Cancelada'.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Sim, cancelar!",
            cancelButtonText: "Não"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoadingAction(true);
                try {
                    const formData = {
                        data_producao: op.data_producao,
                        descricao: op.descricao,
                        observacoes: op.observacoes,
                        status: 'Cancelada',
                        itens: []
                    };
                    await apiCall(`/api/producao/${op.id}`, 'PUT', formData);
                    Swal.fire("Cancelada!", "A Ordem de Produção foi cancelada.", "success");
                    setTriggerFetch(prev => prev + 1);
                } catch (error) {
                    Swal.fire('Erro', error.message, 'error');
                } finally {
                    setLoadingAction(false);
                }
            }
        });
    };

    const handleRefreshInsumos = async (producaoId) => {
        if (!appUserData || !appUserData['editar_producao']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para editar produção.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const op = producoes.find(p => p.id === producaoId);
        if (!op) return;

        if (op.status !== 'Planejada') {
            Swal.fire("Atenção", "A atualização de insumos só é permitida para produções 'Planejadas'.", "warning");
            return;
        }

        Swal.fire({
            title: "Atualizar Insumos?",
            text: "Os insumos serão recalculados com base na ficha técnica atual.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sim, atualizar",
            cancelButtonText: "Cancelar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoadingAction(true);
                try {
                    await apiCall(`/api/producao/${producaoId}/refresh-insumos`, 'POST');
                    Swal.fire("Sucesso!", "Insumos atualizados.", "success");
                    setTriggerFetch(t => t + 1);
                } catch (error) {
                    Swal.fire('Erro', error.message, 'error');
                } finally {
                    setLoadingAction(false);
                }
            }
        });
    };

    // Lógica de Paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentProducoes = producoesFiltradas.slice(indexOfFirstItem, indexOfLastItem);

    // Handlers Paginação
    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handleItemsPerPageChange = (newLimit) => { setItemsPerPage(newLimit); setCurrentPage(1); };

    return (
        <div className="producao-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">

                <div className="producao-header">
                    <h1 className="producao-title">
                        <i className="fas fa-industry me-3 text-primary"></i>
                        Gerenciar <span className="highlight ms-2">Produção</span>
                    </h1>
                </div>

                <main className="producao-content">
                    <div className="producao-controls justify-content-between">
                        <Link to="/app/nova-producao" className="btn-primary-custom">
                            <i className="fas fa-plus me-2"></i>Nova Ordem
                        </Link>
                        <div className="d-flex gap-2">
                            {/* Botão de Filtros */}
                            <button className="btn btn-outline-secondary" onClick={() => setShowFiltros(true)}>
                                <i className="fas fa-filter me-2"></i>Filtros
                                {Object.values(filtrosAplicados).some(v => v.length > 0) && <span className="badge bg-primary ms-2">Ativos</span>}
                            </button>

                            <button className="btn-icon-custom" onClick={() => setTriggerFetch(t => t + 1)} title="Atualizar Lista">
                                <i className={`fas fa-sync-alt ${loadingAction ? 'fa-spin' : ''}`}></i>
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {producoesFiltradas.length === 0 ? (
                                <div className="text-center p-5 text-muted">
                                    <i className="fas fa-clipboard-list fa-3x mb-3"></i>
                                    <p>Nenhuma ordem de produção encontrada.</p>
                                </div>
                            ) : (
                                <ProducaoGrid
                                    arrayProducao={currentProducoes}
                                    clickDelete={deleteProducao}
                                    userPermissions={appUserData}
                                    formatarData={formatarData}
                                    onConcluir={handleConcluirClick}
                                    onEstornar={handleEstornarClick}
                                    onCancelar={handleCancelarClick}
                                    onAtualizarInsumos={handleRefreshInsumos}
                                />
                            )}
                            <Pagination
                                totalItems={producoesFiltradas.length}
                                itemsPerPage={itemsPerPage}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </main>

                {/* --- SIDEBAR DE FILTROS AVANÇADOS --- */}
                <Offcanvas show={showFiltros} onHide={() => setShowFiltros(false)} placement="end" style={{ zIndex: 10500 }}>
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title><i className="fas fa-filter me-2"></i>Filtros Avançados</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <div className="d-grid gap-3" onKeyDown={handleKeyDownFiltros}>

                            <div className="d-grid gap-2 mb-3">
                                <button className="btn btn-primary-custom" onClick={handleAplicarFiltros}>
                                    <i className="fas fa-check me-2"></i>Aplicar Filtros
                                </button>
                                <button className="btn btn-outline-secondary" onClick={handleLimparFiltros}>
                                    <i className="fas fa-eraser me-2"></i>Limpar Filtros
                                </button>
                            </div>
                            <hr className="m-0" />

                            {/* Status (Multi-select via Checkboxes) */}
                            <div className="card bg-light border-0">
                                <div className="card-body">
                                    <h6 className="card-subtitle mb-2 text-muted fw-bold">Status</h6>
                                    <div className="d-flex flex-column gap-2">
                                        {statusOptions.map((opt) => (
                                            <div className="form-check" key={opt}>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="status_check"
                                                    value={opt}
                                                    id={`check-status-${opt}`}
                                                    checked={filtrosForm.status.includes(opt)}
                                                    onChange={handleFiltroChange}
                                                />
                                                <label className="form-check-label ms-2" htmlFor={`check-status-${opt}`}>{opt}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Número */}
                            <div>
                                <label className="form-label small fw-bold">Número OP</label>
                                <input type="text" className="form-control" name="numero" value={filtrosForm.numero} onChange={handleFiltroChange} placeholder="Ex: 1024" />
                            </div>

                            {/* Descrição */}
                            <div>
                                <label className="form-label small fw-bold">Descrição</label>
                                <input type="text" className="form-control" name="descricao" value={filtrosForm.descricao} onChange={handleFiltroChange} placeholder="Ex: Produção Mensal" />
                            </div>

                            {/* Produto */}
                            <div>
                                <label className="form-label small fw-bold">Produto</label>
                                <select className="form-select" name="produtoId" value={filtrosForm.produtoId} onChange={handleFiltroChange}>
                                    <option value="">Todos</option>
                                    {listaProdutos.map(prod => (
                                        <option key={prod.id} value={prod.id}>{prod.codigo} - {prod.descricao}</option>
                                    ))}
                                </select>
                            </div>

                        </div>
                    </Offcanvas.Body>
                </Offcanvas>

                {/* Modals */}
                {showConcluirModal && selectedProducao && (
                    <ModalConcluirProducao
                        isVisible={showConcluirModal}
                        onClose={() => setShowConcluirModal(false)}
                        producaoId={selectedProducao.id}
                        numeroOp={selectedProducao.numero}
                        itensProduzir={detailedProducaoData ? detailedProducaoData.itens_produzir : []}
                        onConclusaoSucesso={() => {
                            setShowConcluirModal(false);
                            setTriggerFetch(t => t + 1);
                        }}
                    />
                )}

                {showEstornarModal && selectedProducao && (
                    <ModalEstornarConclusao
                        isVisible={showEstornarModal}
                        onClose={() => setShowEstornarModal(false)}
                        producaoId={selectedProducao.id}
                        numeroOp={selectedProducao.numero}
                        onEstornoSucesso={() => {
                            setShowEstornarModal(false);
                            setTriggerFetch(t => t + 1);
                        }}
                    />
                )}

                {/* Fim Content With Sidebar */}
            </div>

        </div>
    );
}

export default Producao;
